import React, { useEffect } from 'react';
import { X, Menu as MenuIcon, Info, Zap, Layers, History } from 'lucide-react';
import { DeckKey } from '../types/tarot';
import { IconCircleButton } from './IconCircleButton';

interface MenuModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRequestAddMiniApp?: () => Promise<void> | void;
  onSelectInfo?: () => void;
  activeDeck?: DeckKey;
  onSelectDeck?: (deck: DeckKey) => void;
  deckSwitchDisabled?: boolean;
  standardDeckEnabled?: boolean;
  onSelectHistory?: () => void;
}

export const MenuModal: React.FC<MenuModalProps> = ({
  isOpen,
  onClose,
  onRequestAddMiniApp,
  onSelectInfo,
  activeDeck = 'crypto',
  onSelectDeck,
  deckSwitchDisabled = false,
  standardDeckEnabled = true,
  onSelectHistory,
}) => {
  useEffect(() => {
    if (!isOpen) return undefined;

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleAddMiniApp = async () => {
    try {
      await onRequestAddMiniApp?.();
    } catch (error) {
      console.warn('Failed to trigger add mini app flow:', error);
    }
  };

  const handleAbout = () => {
    onClose();
    onSelectInfo?.();
  };

  const handleHistory = () => {
    onClose();
    onSelectHistory?.();
  };

  const handleDeckSelect = (deck: DeckKey) => {
    if (deckSwitchDisabled) return;
    if (deck !== activeDeck) {
      onSelectDeck?.(deck);
    }
  };

  return (
    <div className="fixed inset-0 z-50">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} aria-hidden />
      <aside className="absolute right-0 top-0 h-full w-72 max-w-full bg-gradient-to-b from-slate-900 to-purple-950 shadow-xl border-l border-purple-500/20 flex flex-col">
        <div className="flex items-center justify-between px-4 py-4 border-b border-white/10">
          <div className="flex items-center space-x-2">
            <MenuIcon className="w-5 h-5 text-purple-200" />
            <h2 className="crypto-heading text-white text-lg">Menu</h2>
          </div>
          <IconCircleButton
            onClick={onClose}
            aria-label="Close"
            size={36}
            icon={<X className="w-4 h-4" />}
          />
        </div>

        <div className="px-4 py-5 space-y-4 text-white/80">
          <button
            onClick={handleAbout}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          >
            <span className="flex items-center space-x-2 text-sm font-medium text-white">
              <Info className="w-4 h-4" />
              <span>About app</span>
            </span>
          </button>

          <button
            onClick={handleHistory}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-white/5 hover:bg-white/10 transition-colors"
          >
            <span className="flex items-center space-x-2 text-sm font-medium text-white">
              <History className="w-4 h-4" />
              <span>Reading history</span>
            </span>
          </button>

          {standardDeckEnabled && (
            <div className="rounded-lg bg-white/5 border border-white/10 p-4">
              <div className="flex items-center justify-between mb-3">
                <span className="flex items-center space-x-2 text-sm font-medium text-white">
                  <Layers className="w-4 h-4 text-white/70" />
                  <span>Decks</span>
                </span>
              </div>
              <div className="relative">
                <div
                  className={`bg-white/10 rounded-full p-1 flex items-center text-xs font-semibold transition-colors ${
                    deckSwitchDisabled ? 'opacity-50 cursor-not-allowed' : 'text-white/70'
                  }`}
                >
                  <div
                    className="absolute top-1 bottom-1 w-1/2 rounded-full bg-purple-600 transition-transform duration-300 ease-in-out"
                    style={{ transform: activeDeck === 'standard' ? 'translateX(100%)' : 'translateX(0%)' }}
                  />
                  <button
                    onClick={() => handleDeckSelect('crypto')}
                    disabled={deckSwitchDisabled}
                    className={`relative z-10 flex-1 py-1 rounded-full transition-colors ${
                      activeDeck === 'crypto' ? 'text-white' : ''
                    }`}
                  >
                    Crypto
                  </button>
                  <button
                    onClick={() => handleDeckSelect('standard')}
                    disabled={deckSwitchDisabled}
                    className={`relative z-10 flex-1 py-1 rounded-full transition-colors ${
                      activeDeck === 'standard' ? 'text-white' : ''
                    }`}
                  >
                    Standard
                  </button>
                </div>
              </div>
              <p className="mt-3 text-xs text-white/60">
                The Deck: 50 crypto tarot cards, or 78 classic tarot cards, each with its own energy and meaning.
              </p>
            </div>
          )}

          <button
            onClick={handleAddMiniApp}
            className="w-full flex items-center justify-between px-3 py-2 rounded-lg bg-purple-600/20 border border-purple-500/40 text-sm font-medium text-white hover:bg-purple-600/30 transition-colors"
          >
            <span className="flex items-center space-x-2">
              <Zap className="w-4 h-4" />
              <span>Add to Farcaster</span>
            </span>
          </button>
        </div>

        <div className="flex-1" />
      </aside>
    </div>
  );
};
